import { useEffect, useRef } from 'react';

/**
 * Full-screen premium gold confetti overlay. Renders nothing until `active` is
 * true, then bursts particles from the bottom corners and drizzles from above.
 * Pure canvas — no library dependency.
 */
export default function GoldConfetti({ active }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
    };
    resize();

    const W = () => window.innerWidth;
    const H = () => window.innerHeight;

    const COLORS = [
      '#FFD24D', '#F5C242', '#E8A93B', '#FFF1B8',
      '#FFB347', '#D4AF37', '#FFE066', '#B8860B',
    ];

    const SHAPES = ['rect', 'circle', 'sparkle'];

    const particles = [];
    const SPAWN_COUNT = 220;

    for (let i = 0; i < SPAWN_COUNT; i++) {
      const fromLeft = i % 2 === 0;
      const ox = fromLeft ? W() * 0.15 : W() * 0.85;
      const oy = H() * 0.6;
      const angle = fromLeft
        ? (-Math.PI / 2) + (Math.random() * 0.9 - 0.2)
        : (-Math.PI / 2) + (Math.random() * 0.9 - 0.7);
      const speed = 8 + Math.random() * 11;
      particles.push({
        x: ox,
        y: oy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 5 + Math.random() * 9,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.25,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
        life: 0,
        maxLife: 200 + Math.random() * 80,
      });
    }

    let drizzleAccum = 0;
    const spawnDrizzle = () => {
      drizzleAccum++;
      if (drizzleAccum < 2) return;
      drizzleAccum = 0;
      for (let i = 0; i < 4; i++) {
        particles.push({
          x: Math.random() * W(),
          y: -20,
          vx: (Math.random() - 0.5) * 2,
          vy: 2 + Math.random() * 3,
          size: 4 + Math.random() * 7,
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.18,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
          life: 0,
          maxLife: 260 + Math.random() * 80,
        });
      }
    };

    const drawSparkle = (size) => {
      ctx.beginPath();
      const s = size;
      for (let k = 0; k < 4; k++) {
        const a = (k * Math.PI) / 2;
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(a) * s, Math.sin(a) * s);
      }
      ctx.lineWidth = 1.6;
      ctx.stroke();
    };

    let raf;
    let frame = 0;
    const GRAVITY = 0.22;
    const DRAG = 0.992;

    const tick = () => {
      ctx.clearRect(0, 0, W(), H());
      if (frame < 90) spawnDrizzle();
      frame++;

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life++;

        const alpha = Math.max(0, 1 - p.life / p.maxLife);
        if (p.y > H() + 40 || alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.strokeStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          drawSparkle(p.size);
        }
        ctx.restore();
      }

      if (particles.length > 0) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[100]"
      aria-hidden="true"
    />
  );
}
